import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getFbStatus,
  getFbAdAccounts,
  getMetaAppConfig,
  saveMetaAppConfig,
  selectFbAccount,
  disconnectFb,
  getFbConnectUrl,
  FbStatus,
  AdAccount,
} from '../api/facebook'
import apiClient from '../api/client'
import { useAuth } from '../context/AuthContext'
import styles from './SettingsPage.module.css'

export function SettingsPage() {
  const [searchParams] = useSearchParams()
  const { email } = useAuth()

  const [fbStatus, setFbStatus] = useState<FbStatus | null>(null)
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [actionMsg, setActionMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // App credentials
  const [appConfigured, setAppConfigured] = useState(false)
  const [appId, setAppId] = useState('')
  const [appSecret, setAppSecret] = useState('')
  const [savingApp, setSavingApp] = useState(false)
  const [editingApp, setEditingApp] = useState(false)

  // Page / ad account
  const [pageId, setPageId] = useState('')
  const [savingPage, setSavingPage] = useState(false)
  const [editingPage, setEditingPage] = useState(false)

  const fbConnected = searchParams.get('fb_connected')
  const fbError = searchParams.get('fb_error')

  useEffect(() => {
    if (fbConnected === 'true') setActionMsg('Facebook account connected successfully!')
    else if (fbError) setError(`Facebook connection failed: ${fbError}`)
  }, [fbConnected, fbError])

  const loadStatus = async () => {
    setLoading(true)
    try {
      const [status, config] = await Promise.all([getFbStatus(), getMetaAppConfig()])
      setFbStatus(status)
      setAppConfigured(Boolean(config.configured))
      setAppId(config.app_id ?? '')
      setPageId(status.page_id ?? '')
      if (status.connected) {
        const { ad_accounts } = await getFbAdAccounts()
        setAdAccounts(ad_accounts)
      }
    } catch {
      setError('Failed to load Facebook status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadStatus() }, [])

  const handleSaveApp = async () => {
    setSavingApp(true)
    setError(null)
    setActionMsg(null)
    try {
      const config = await saveMetaAppConfig(appId, appSecret)
      setAppConfigured(Boolean(config.configured))
      setAppId(config.app_id ?? appId)
      setAppSecret('')
      setEditingApp(false)
      setActionMsg('Meta App credentials saved.')
    } catch {
      setError('Failed to save Meta App credentials.')
    } finally {
      setSavingApp(false)
    }
  }

  const handleConnect = () => {
    if (!appConfigured) {
      setError('Save your Meta App ID and App Secret before connecting Facebook.')
      return
    }
    window.location.href = getFbConnectUrl()
  }

  const handleSavePage = async () => {
    if (!pageId.trim()) return
    setSavingPage(true)
    setError(null)
    setActionMsg(null)
    try {
      await apiClient.post('/facebook/select-account', {
        ad_account_id: fbStatus?.ad_account_id || '',
        page_id: pageId.trim(),
      })
      setActionMsg('Facebook Page ID saved.')
      setEditingPage(false)
      await loadStatus()
    } catch {
      setError('Failed to save Page ID.')
    } finally {
      setSavingPage(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      await disconnectFb()
      setFbStatus({ connected: false, app_configured: appConfigured, app_id: appId })
      setAdAccounts([])
      setActionMsg('Facebook account disconnected.')
    } catch {
      setError('Failed to disconnect Facebook account.')
    }
  }

  const handleSelectAccount = async (accountId: string) => {
    try {
      await selectFbAccount(accountId, pageId)
      setActionMsg(`Ad account ${accountId} selected.`)
      await loadStatus()
    } catch {
      setError('Failed to update ad account.')
    }
  }

  // Determine overall setup state
  const fullySetUp = appConfigured && fbStatus?.connected && !!fbStatus?.page_id

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Settings</h1>

      {/* Account Info */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>🔑 Account</h2>
        </div>
        <div className={styles.connectedSection}>
          <p className={styles.description}>
            All your settings are saved to this account. You only need to set things up once.
          </p>
          <div className={styles.statusBadge}>
            <span className={styles.dot} /> {email}
          </div>
        </div>
      </div>

      {/* Fully set up banner */}
      {fullySetUp && (
        <div className={styles.allSetBanner}>
          <span>✅</span>
          <div>
            <strong>Everything is set up!</strong>
            <span> Your Meta App, Facebook account, and Page ID are all saved. You can go straight to creating campaigns.</span>
          </div>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png"
            alt="Facebook"
            className={styles.fbLogo}
          />
          <h2 className={styles.cardTitle}>Facebook Account</h2>
          {appConfigured && (
            <span className={styles.statusBadge} style={{ marginLeft: 'auto', fontSize: '0.72rem' }}>
              <span className={styles.dot} /> Configured
            </span>
          )}
        </div>

        {actionMsg && <div className={styles.success}>{actionMsg}</div>}
        {error && <div className={styles.error}>{error}</div>}

        {loading ? (
          <p className={styles.loading}>Loading saved settings...</p>
        ) : (
          <>
            {/* ── Step 1: Meta App Credentials ── */}
            <div className={styles.setupStep}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNum}>{appConfigured ? '✅' : '1'}</span>
                <div>
                  <strong>Meta App Credentials</strong>
                  <p className={styles.stepDesc}>
                    {appConfigured
                      ? `App ID saved: ${appId}`
                      : 'Enter your Meta App ID and Secret (one-time setup).'}
                  </p>
                </div>
                {appConfigured && !editingApp && (
                  <button className={styles.editBtn} onClick={() => setEditingApp(true)}>
                    Edit
                  </button>
                )}
              </div>

              {(!appConfigured || editingApp) && (
                <div className={styles.appConfig}>
                  <input
                    className={styles.input}
                    value={appId}
                    onChange={e => setAppId(e.target.value)}
                    placeholder="Meta App ID"
                  />
                  <input
                    className={styles.input}
                    value={appSecret}
                    onChange={e => setAppSecret(e.target.value)}
                    placeholder={appConfigured ? 'New App Secret (leave blank to keep current)' : 'Meta App Secret'}
                    type="password"
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={styles.saveBtn}
                      onClick={handleSaveApp}
                      disabled={savingApp || !appId || (!appConfigured && !appSecret)}
                    >
                      {savingApp ? 'Saving...' : appConfigured ? 'Update' : 'Save'}
                    </button>
                    {editingApp && (
                      <button className={styles.cancelBtn} onClick={() => { setEditingApp(false); setAppSecret('') }}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Step 2: Facebook Connection ── */}
            <div className={styles.setupStep}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNum}>{fbStatus?.connected ? '✅' : '2'}</span>
                <div>
                  <strong>Facebook Account</strong>
                  <p className={styles.stepDesc}>
                    {fbStatus?.connected
                      ? `Connected · Ad Account: ${fbStatus.ad_account_id || 'not set'}`
                      : 'Connect your Facebook account to manage ads.'}
                  </p>
                </div>
              </div>

              {fbStatus?.connected ? (
                <div style={{ marginTop: '0.5rem' }}>
                  {adAccounts.length > 1 && (
                    <div className={styles.accountList}>
                      <p className={styles.accountListLabel}>Switch ad account:</p>
                      {adAccounts.map(acc => (
                        <div key={acc.id} className={styles.accountRow}>
                          <span>{acc.name} ({acc.id})</span>
                          <button
                            className={acc.id === fbStatus.ad_account_id ? styles.selectedBtn : styles.selectBtn}
                            onClick={() => handleSelectAccount(acc.id)}
                            disabled={acc.id === fbStatus.ad_account_id}
                          >
                            {acc.id === fbStatus.ad_account_id ? 'Active' : 'Select'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className={styles.disconnectBtn} onClick={handleDisconnect}>
                    Disconnect Facebook
                  </button>
                </div>
              ) : (
                <button
                  className={styles.connectBtn}
                  onClick={handleConnect}
                  disabled={!appConfigured}
                  title={!appConfigured ? 'Save Meta App credentials first' : ''}
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png"
                    alt=""
                    className={styles.btnLogo}
                  />
                  Connect Facebook Account
                </button>
              )}
            </div>

            {/* ── Step 3: Page ID ── */}
            {fbStatus?.connected && (
              <div className={styles.setupStep}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepNum}>{fbStatus.page_id ? '✅' : '3'}</span>
                  <div>
                    <strong>Facebook Page ID</strong>
                    <p className={styles.stepDesc}>
                      {fbStatus.page_id
                        ? `Page ID saved: ${fbStatus.page_id}`
                        : 'Required for publishing ads. Find it on your Facebook Page → About.'}
                    </p>
                  </div>
                  {fbStatus.page_id && !editingPage && (
                    <button className={styles.editBtn} onClick={() => setEditingPage(true)}>
                      Edit
                    </button>
                  )}
                </div>

                {(!fbStatus.page_id || editingPage) && (
                  <div className={styles.pageIdSection}>
                    <div className={styles.pageIdRow}>
                      <input
                        className={styles.input}
                        value={pageId}
                        onChange={e => setPageId(e.target.value)}
                        placeholder="e.g. 988433661017999"
                      />
                      <button
                        className={styles.saveBtn}
                        onClick={handleSavePage}
                        disabled={savingPage || !pageId.trim()}
                      >
                        {savingPage ? 'Saving...' : 'Save'}
                      </button>
                      {editingPage && (
                        <button className={styles.cancelBtn} onClick={() => { setEditingPage(false); setPageId(fbStatus.page_id ?? '') }}>
                          Cancel
                        </button>
                      )}
                    </div>
                    <p className={styles.hint}>
                      Find your Page ID: go to your Facebook Page → About → scroll to bottom.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
